import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import {
  FaQrcode,
  FaHandSparkles,
  FaInstagram,
  FaFacebook,
} from "react-icons/fa";
import { GiScissors } from "react-icons/gi";
import "./App.css";

const Home = () => (
  <div className="container">
    {/* Hero Section */}
    <section className="hero">
      <div className="hero-text">
        <h1 className="salon-title">SalonSnap</h1>
        <p className="hero-description">
          SalonSnap helps you find the best hair salons tailored to your style
          and manage loyalty rewards at participating beauty and wellness
          businesses. Customers never lose progress toward discounts thanks to a
          single QR code in their profile, while salons can subscribe to manage
          loyalty programs.
        </p>
        <div className="badges">
          <a
            href="https://play.google.com/store/apps/details?id=com.emuolanari.salonsnap"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src="/assets/google-play-badge.png" alt="Google Play" />
          </a>
          <a
            href="https://apps.apple.com/gb/app/salonsnap/id6748883100"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src="/assets/app-store-badge.svg" alt="App Store" />
          </a>
        </div>
      </div>
    </section>

    {/* Features Section */}
    <section className="features">
      <h2>Why Choose SalonSnap?</h2>
      <div className="feature-grid">
        <div className="feature-card">
          <GiScissors size={40} color="#2d0689" />
          <h3>Hair Salon Discovery</h3>
          <p>
            Find hair salons near you based on gender, ethnicity, hair type, and
            distance. See reviews from Google and users, add your own reviews,
            call salons directly, and view more images.
          </p>
        </div>

        <div className="feature-card">
          <FaHandSparkles size={40} color="#2d0689" />
          <h3>Loyalty</h3>
          <p>
            Replace paper loyalty cards with a single QR code in your profile at
            participating salons. Salons subscribe to scan QR codes and manage
            loyalty rewards — no printed cards required.
          </p>
        </div>

        <div className="feature-card">
          <FaQrcode size={40} color="#2d0689" />
          <h3>Simple QR Scanning</h3>
          <p>
            Customers maintain a single QR code for loyalty. Salons subscribe to
            access our QR scanner and manage loyalty efficiently. Progress
            toward discounts is recorded automatically.
          </p>
        </div>
      </div>
    </section>

    {/* App Screenshots Section */}
    <section className="screenshot-section">
      <h2>Explore the App</h2>

      <h3 className="screenshot-subtitle">Hair Salon Discovery</h3>
      <div className="screenshot-grid">
        <div className="screenshot-card">
          <img
            src="/assets/home.jpg"
            alt="Home Screen"
            className="screenshot"
          />
          <p>Home screen to start hair salon discovery or access loyalty.</p>
        </div>
        <div className="screenshot-card">
          <img
            src="/assets/hairsalonssearch.jpg"
            alt="Hair Salon Search"
            className="screenshot"
          />
          <p>Search for hair salons near you with filters.</p>
        </div>
        <div className="screenshot-card">
          <img
            src="/assets/filters.jpg"
            alt="Hair Salon filters"
            className="screenshot"
          />
          <p>Filter salons by hair type, gender, and ethnicity.</p>
        </div>
        <div className="screenshot-card">
          <img
            src="/assets/salonscreen.jpg"
            alt="Hair Salon Screen"
            className="screenshot"
          />
          <p>View salon details, reviews, and call to book.</p>
        </div>
        <div className="screenshot-card">
          <img
            src="/assets/addreview.jpg"
            alt="Add review for hair salon"
            className="screenshot"
          />
          <p>Submit your own review for a salon.</p>
        </div>
      </div>

      <h3 className="screenshot-subtitle">Loyalty</h3>
      <div className="screenshot-grid">
        <div className="screenshot-card">
          <img
            src="/assets/signup.jpg"
            alt="Sign-up Screen"
            className="screenshot"
          />
          <p>Sign up to access loyalty features.</p>
        </div>
        <div className="screenshot-card">
          <img
            src="/assets/salondashboard.jpg"
            alt="Business dashboard"
            className="screenshot"
          />
          <p>Dashboard for managing loyalty scans and subscriptions.</p>
        </div>
        <div className="screenshot-card">
          <img
            src="/assets/loyalty-qr.jpg"
            alt="Customer QR Code"
            className="screenshot"
          />
          <p>Customer profile with a single QR code for loyalty tracking.</p>
        </div>
        <div className="screenshot-card">
          <img
            src="/assets/subscription.jpg"
            alt="Subscription page"
            className="screenshot"
          />
          <p>Subscribe to unlock the QR scanning feature.</p>
        </div>
        <div className="screenshot-card">
          <img
            src="/assets/stripeform.jpg"
            alt="Stripe payment form"
            className="screenshot"
          />
          <p>Stripe payment form for salon subscriptions.</p>
        </div>
      </div>
    </section>

    <p className="policy-links">
      <Link to="/privacy" className="privacy-link">
        Read our Privacy Policy
      </Link>{" "}
      ·{" "}
      <Link to="/delete-account" className="delete-link">
        Delete Account & Data
      </Link>
    </p>

    <footer className="footer">
      <div className="social-links">
        <a
          href="https://www.instagram.com/salonsnap_"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
        >
          <FaInstagram size={28} color="#fff" />
        </a>

        <a
          href="https://www.facebook.com/profile.php?id=61583882742166"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Facebook"
        >
          <FaFacebook size={28} color="#fff" />
        </a>
      </div>
      <p>© {new Date().getFullYear()} SalonSnap. All rights reserved.</p>
      <p className="footer-links">
        <Link to="/privacy">Privacy</Link> ·{" "}
        <Link to="/delete-account">Delete Account</Link>
      </p>
    </footer>
  </div>
);

const Privacy = () => (
  <div className="privacy-container">
    <h1>Privacy Policy</h1>
    <p>
      <strong>Effective Date:</strong> September 9, 2025
    </p>

    <p>
      SalonSnap respects your privacy. This Policy explains what we collect, how
      we use it, when we share it, your choices, and how to contact us.
    </p>

    <h2>1) What We Collect</h2>
    <ul>
      <li>
        <strong>Location Data:</strong> Used to find nearby hair salons. Can be
        revoked in settings.
      </li>
      <li>
        <strong>Identifiers:</strong> Firebase Auth IDs, device IDs.
      </li>
      <li>
        <strong>User Information:</strong> Name, email address (provided during
        sign-up).
      </li>
      <li>
        <strong>Salon Information:</strong> Name, address, phone number, images.
      </li>
      <li>
        <strong>Device & Network Info:</strong> IP, device info, crash logs.
      </li>
      <li>
        <strong>User Content:</strong> Optional reviews.
      </li>
      <li>
        <strong>Loyalty & Subscription Data:</strong> Customer QR codes, visits,
        loyalty progress; Salon subscription status, scan logs, billing info
        (Stripe).
      </li>
    </ul>

    <h2>2) How We Use Data</h2>
    <ul>
      <li>Hair salon discovery: show salons, reviews, call, images.</li>
      <li>Loyalty rewards: track QR scans and reward progress.</li>
      <li>Salon subscriptions: Stripe handles payments; can cancel anytime.</li>
      <li>Diagnostics & Security: prevent fraud and improve stability.</li>
      <li>Ads: optional, based on consent.</li>
    </ul>

    <h2>3) Tracking & Consent</h2>
    <p>iOS ATT prompt and Google UMP used where applicable.</p>

    <h2>4) Data Sharing</h2>
    <ul>
      <li>Stripe: payment processing.</li>
      <li>AdMob: ads.</li>
      <li>Firebase: Auth, Firestore, Cloud Functions.</li>
    </ul>

    <h2>5) Legal Bases</h2>
    <ul>
      <li>Consent: ads & tracking.</li>
      <li>Contract: salon subscriptions.</li>
      <li>Legitimate interests: app functionality & fraud prevention.</li>
    </ul>

    <h2>6) Your Choices</h2>
    <ul>
      <li>iOS tracking settings.</li>
      <li>Ad personalization via UMP.</li>
      <li>Location access settings.</li>
      <li>Cancel salon subscriptions anytime.</li>
      <li>Request review deletion.</li>
    </ul>

    <h2>7) Data Retention</h2>
    <ul>
      <li>Customer loyalty data: until account deletion/request.</li>
      <li>Salon subscription data: while active, minimal billing retention.</li>
      <li>Logs/security: typically up to 90 days.</li>
    </ul>

    <h2>8) Children’s Privacy</h2>
    <p>Not directed to children under 13. Contact us for deletion requests.</p>

    <h2>9) International Transfers</h2>
    <p>
      Data may be processed on servers outside your country with standard
      contractual clauses.
    </p>

    <h2>10) Security</h2>
    <p>Technical and organizational safeguards implemented to protect data.</p>

    <h2>11) Your Rights</h2>
    <p>
      Request access, correction, deletion, restriction, or withdraw consent
      depending on location.
    </p>

    <h2>12) Third-Party Policies</h2>
    <ul>
      <li>
        <a
          href="https://stripe.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Stripe Privacy Policy
        </a>
      </li>
      <li>
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Privacy Policy
        </a>
      </li>
      <li>
        <a
          href="https://developers.google.com/admob/ios/privacy/data-disclosure"
          target="_blank"
          rel="noopener noreferrer"
        >
          AdMob Data Disclosure
        </a>
      </li>
      <li>
        <a
          href="https://support.apple.com/en-us/HT212025"
          target="_blank"
          rel="noopener noreferrer"
        >
          Apple ATT
        </a>
      </li>
      <li>
        <a
          href="https://developers.google.com/admob/ump"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google UMP
        </a>
      </li>
    </ul>

    <h2>13) Changes</h2>
    <p>Updates appear here and may be communicated in-app.</p>

    <h2>14) Contact</h2>
    <p>
      Email: <a href="mailto:support@salonsnap.app">support@salonsnap.app</a>
    </p>

    <p className="back-link">
      <Link to="/">← Back to Home</Link>
    </p>
  </div>
);

/** Delete Account & Data page (meets Google Play requirements) */
const DeleteAccount = () => (
  <div className="policy-container">
    <h1>Delete Account &amp; Data — SalonSnap</h1>
    <p>SalonSnap</p>
    <p>
      <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
    </p>

    <h2>How to Request Deletion</h2>
    <ol>
      <li>
        Email <a href="mailto:support@salonsnap.app">support@salonsnap.app</a>{" "}
        <strong>from the same email address you used to sign up</strong>.
      </li>
      <li>
        Use the subject line: <code>Delete My Account</code>.
      </li>
    </ol>

    <h2>What Will Be Deleted</h2>
    <ul>
      <li>Your SalonSnap account and login credentials.</li>
      <li>
        Personal data stored in our systems (profile details and loyalty
        progress associated with your account).
      </li>
    </ul>

    <h2>What May Be Retained and Why</h2>
    <ul>
      <li>
        <strong>Payment/Subscription Records</strong> (handled by Stripe): kept
        as minimally required for tax, fraud prevention, and accounting
        compliance.
      </li>
      <li>
        <strong>Server Logs/Security Records:</strong> may be retained for a
        limited period (typically up to 90 days) to ensure platform integrity.
      </li>
      <li>
        <strong>Anonymous, Aggregated Analytics:</strong> non-identifiable and
        cannot be linked back to you.
      </li>
    </ul>

    <h2>Processing Time</h2>
    <p>
      We aim to complete verified deletion requests within{" "}
      <strong>30 days</strong>. We may contact you if we need additional
      information to verify ownership.
    </p>

    <h2>Verification</h2>
    <p>
      For your security, requests must come from the email address associated
      with your SalonSnap account. If you can’t access that email, contact us
      and we’ll provide alternative verification steps.
    </p>

    <h2>Questions</h2>
    <p>
      Need help? Email{" "}
      <a href="mailto:support@salonsnap.app">support@salonsnap.app</a>.
    </p>

    <p className="back-link">
      <Link to="/">← Back to Home</Link>
    </p>
  </div>
);

const App = () => (
  <Router>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
    </Routes>
  </Router>
);

export default App;
